import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getVoiceProduct } from "../../../../shared/hooks/useFetchServiceDetails";
import API_URLS from "../../Utils/axr";
import store from "../store";
import DwLogutil from "../../Utils/DwLogutil";
import { errorConditionGTM } from "../../Utils/googleTags";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";

type MoversVoiceProductStateType = {
  moversVoiceProductData: any;
  voiceAPILoading: boolean;
  error: any;
};

const initialState: MoversVoiceProductStateType = {
  moversVoiceProductData: null,
  voiceAPILoading: false,
  error: null,
};

// Async thunk
export const getMoversVoiceProducts = createAsyncThunk(
  "moversVoiceProduct/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const reduxStore = store.getState();
      const headers = reduxStore?.brightspeedToken?.headers;
      const moversAddressQualificationData = reduxStore?.addressQualification?.moversData?.moversAddressQualification?.entity;

      const siteId =
        moversAddressQualificationData?.siteId ||
        headers?.siteId?.toLowerCase();

      const wirecenter =
        moversAddressQualificationData?.wireCenter ||
        reduxStore?.addressQualification?.data?.addressQualification?.entity?.wireCenter;

      const planDetails = reduxStore?.planDetails?.planDetails;
      const tnNumber = planDetails?.pricing?.pots?.[0]?.tnNumber;
      
      const bundleCode = new Set();
      const pricePlanCode = new Set();
      const featureCodeList = new Set();

      planDetails?.standardPlans?.forEach((subInfo: any) => {
        if (tnNumber === subInfo?.productSku) {
          bundleCode.add(subInfo?.bundleCode);
          pricePlanCode.add(subInfo?.productRatePlanId);
        }
      });

      for (const product of planDetails?.existingOptionalFeatureDetail?.[0]?.existingOptionalFeatureDetails ?? []) {
        featureCodeList.add(product?.featureCode);
      }

      const productRequest = {
        productCode: "",
        wirecenter: wirecenter,
        addressDetailId: moversAddressQualificationData?.addressId,
        billingType: "postpaid",
        promoCode: "",
        partnerOrderId: moversAddressQualificationData?.partnerSessionId,
        interestedInSelfInstallOrTechInstall: "Tech",
        propertyId: moversAddressQualificationData?.propertyId,
        customerType: "",
        lineType: "",
        speed: "",
        bundleCode: Array.from(bundleCode),
        pricePlanCode: Array.from(pricePlanCode),
        featureCodeList: Array.from(featureCodeList),
        fiberVoiceEligible: false,
        productType: "FV",
      };

      const payload = {
        baseline: {
          transactionId: moversAddressQualificationData?.partnerSessionId,
          channel: "web-acm",
          siteId:moversAddressQualificationData?.siteId,
          wirecenter: wirecenter,
          intent: "",
          partnerOrderId: moversAddressQualificationData?.partnerSessionId,
          workflow: "mover",
          billingType: "postpaid",
        },
        productRequest,
      };

      const params = { "": [API_URLS?.getVoiceProductUrl] };

      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.movers_voice_product_api_triggered });

      const response = await getVoiceProduct(params, payload, headers);

      // Filter voice plans similar to getV2PostPaidVoiceProducts
      if (reduxStore?.planDetails?.ctlPlanDetails && Array.isArray(reduxStore?.planDetails?.ctlPlanDetails) && reduxStore?.planDetails?.ctlPlanDetails.length) {
        const voicePlanName = reduxStore?.planDetails?.ctlPlanDetails[0]?.pots[0]?.productIdDescription;
        let productGroups = response?.data?.productResponse?.product?.productGroups;
        const indexToRemove = productGroups?.findIndex((product: any) => (product?.productName?.toLowerCase()?.includes("fiber voice") && product?.productName?.trim() !== voicePlanName?.trim()));
        if (indexToRemove > -1) {
          productGroups.splice(indexToRemove, 1);
        }
      }

      DwLogutil({
        stepName:
          Number(response?.data?.messageStatus?.[0]?.statusCode) === 200
            ? EC_DW_LOGGING_STEPS.movers_voice_product_api_success
            : EC_DW_LOGGING_STEPS.movers_voice_product_api_failed,
        error:
          Number(response?.data?.messageStatus?.[0]?.statusCode) !== 200
            ? response?.data?.messageStatus?.[0]?.statusMessage
            : undefined,
      });

      return response?.data || response;
    } catch (error: any) {
      const errorMessage =
        error?.message || "Something went wrong while fetching Movers Voice Product";

      DwLogutil({
        stepName: EC_DW_LOGGING_STEPS.movers_voice_product_api_failed,
        error: errorMessage,
      });

      return rejectWithValue(errorMessage);
    }
  }
);

// Slice
const moversVoiceProductSlice = createSlice({
  name: "moversVoiceProduct",
  initialState,
  reducers: {
    clearMoversVoiceProductSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMoversVoiceProducts.pending, (state) => {
        state.voiceAPILoading = true;
        state.error = null;
      })
      .addCase(
        getMoversVoiceProducts.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.voiceAPILoading = false;
          state.moversVoiceProductData = action.payload;
          state.error = null;
        }
      )
      .addCase(getMoversVoiceProducts.rejected, (state, action) => {
        state.voiceAPILoading = false;
        state.moversVoiceProductData = null;
        state.error = action.payload;

        errorConditionGTM(
          "BRSPD_Fiber_EC_Flow",
          "movers voice product",
          action.error?.message,
          action?.type
        );
      });
  },
});

export const { clearMoversVoiceProductSlice } = moversVoiceProductSlice.actions;
export default moversVoiceProductSlice.reducer;
